import { airportLocalTime, formatAirportLocalDateTime } from '@insurwiz/wallet-core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { deletePass, getPass, getPassDesign } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { WalletSaveCard } from '../components/WalletSaveCard';
import { PassCanvas } from '../components/studio/PassCanvas';
import { STUDIO_APPLE_STYLE, type StudioTemplateKind } from '../components/studio/templateCatalog';
import { useI18n, type Lang, type Translate } from '../i18n';
import { describeApiError, isNotFound } from '../lib/apiErrors';
import { designImageUrl } from '../lib/designImages';
import { passPreviewDesign } from '../lib/passPreview';
import type { DemoPass } from '../types';

function dateLocale(lang: Lang): string {
  return lang === 'hu' ? 'hu-HU' : 'en-GB';
}

function formatDateTime(iso: string, lang: Lang): string {
  return new Date(iso).toLocaleString(dateLocale(lang), {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDate(iso: string, lang: Lang): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(dateLocale(lang), {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

/** Trimmed string read from an untyped servicePayload — null when absent/blank. */
function payloadString(sp: Record<string, unknown>, key: string): string | null {
  const v = sp[key];
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

/**
 * Until when the Wallet shows a flight pass (spec 2026-08-31 Part A): the
 * operator's absolute `visibleUntil`, else scheduled arrival + the chosen
 * hours (default 6). Visibility only — the pass keeps updating either way.
 */
function walletVisibleUntil(pass: DemoPass): string | null {
  const sp = pass.servicePayload ?? {};
  const until = sp['visibleUntil'];
  if (typeof until === 'string' && !Number.isNaN(Date.parse(until))) return until;
  if (!pass.scheduledArrivalUtc) return null;
  const arrivalMs = Date.parse(pass.scheduledArrivalUtc);
  if (Number.isNaN(arrivalMs)) return null;
  const hours = sp['visibleAfterArrivalHours'];
  const h = typeof hours === 'number' && Number.isFinite(hours) ? hours : 6;
  return new Date(arrivalMs + h * 60 * 60 * 1000).toISOString();
}

export function PassDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { partner } = useAuth();
  const { lang, t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const passQuery = useQuery({
    queryKey: ['pass', id],
    queryFn: () => getPass(id),
    refetchInterval: 30_000,
    // A deleted pass stays deleted — retrying a 404 only delays the answer.
    retry: (count, error) => !isNotFound(error) && count < 1,
  });
  const pass = passQuery.data;

  // The studio template of this pass fixes its Apple layout (utazás →
  // boardingPass, általános → generic); legacy template kinds have none.
  const studioKind =
    pass && (pass.templateKind === 'travel' || pass.templateKind === 'assistance')
      ? (pass.templateKind as StudioTemplateKind)
      : null;
  const designQuery = useQuery({
    queryKey: ['passDesign', studioKind, studioKind ? STUDIO_APPLE_STYLE[studioKind] : null],
    queryFn: () => getPassDesign(studioKind!, STUDIO_APPLE_STYLE[studioKind!]),
    enabled: studioKind !== null,
    staleTime: 60_000,
  });

  const [confirming, setConfirming] = useState(false);
  const deleteMutation = useMutation({
    mutationFn: () => deletePass(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['passes'] });
      navigate('/dashboard');
    },
  });

  if (passQuery.isLoading) {
    return (
      <div className="grid place-items-center min-h-[40vh] text-slate-500">
        {t('common.loading')}
      </div>
    );
  }

  if (!pass) {
    // An honest end state instead of an endless "Betöltés…": the pass is
    // gone (deleted, stale bookmark) or the load failed (retry in place).
    const gone = isNotFound(passQuery.error);
    return (
      <div className="max-w-md mx-auto px-4 sm:px-6 py-16 text-center space-y-4">
        <h1 className="text-xl font-semibold text-slate-900">
          {gone ? t('detail.notFoundTitle') : t('detail.loadFailedTitle')}
        </h1>
        <p className="text-sm text-slate-600">
          {gone ? t('detail.notFoundBody') : describeApiError(passQuery.error, t)}
        </p>
        <div className="flex items-center justify-center gap-3">
          {gone ? null : (
            <button
              type="button"
              onClick={() => void passQuery.refetch()}
              disabled={passQuery.isFetching}
              className="btn-secondary"
            >
              {t('common.retry')}
            </button>
          )}
          <Link to="/dashboard" className="btn-primary">
            {t('detail.toDashboard')}
          </Link>
        </div>
      </div>
    );
  }

  // Template-aware rendering: only the travel template has a flight (and a
  // flight subscription behind the live status) — every other template
  // renders its servicePayload fields. The studio flight is OPTIONAL, so a
  // travel pass may be flightless: travel layout, but NO live UI.
  const isTravel = pass.templateKind === 'travel';
  const hasFlight = isTravel && Boolean(pass.flightIata);
  const preview =
    designQuery.data !== undefined
      ? passPreviewDesign(pass, designQuery.data.design, partner)
      : null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
      <Link to="/dashboard" className="text-sm text-brand-700 hover:underline">
        {t('detail.back')}
      </Link>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_380px] gap-8 mt-4">
        <div className="space-y-6 min-w-0">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 break-words">{pass.recipientName}</h1>
            <p className="text-slate-500 break-words">{pass.recipientEmail}</p>
          </div>

          <section className="card p-5 sm:p-6 space-y-4">
            <div>
              <h2 className="font-semibold text-slate-900">{t('detail.walletAddTitle')}</h2>
              <p className="text-sm text-slate-500 mt-1">{t('detail.walletAddBody')}</p>
            </div>
            <WalletSaveCard appleSaveUrl={pass.appleSaveUrl} googleSaveUrl={pass.googleSaveUrl} />
          </section>

          {/* Live flight status only exists on a FLIGHT-BOUND travel pass —
              non-travel and flightless travel passes have no subscription. */}
          {hasFlight ? <LiveStatusCard pass={pass} lang={lang} t={t} /> : null}

          {isTravel ? (
            <TravelDetailsCard pass={pass} lang={lang} t={t} />
          ) : (
            <ServiceDetailsCard pass={pass} lang={lang} t={t} />
          )}

          <div className="space-y-2">
            {confirming ? (
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="text-slate-700">{t('detail.confirmDelete')}</span>
                <button
                  type="button"
                  onClick={() => deleteMutation.mutate()}
                  disabled={deleteMutation.isPending}
                  className="font-medium text-red-700 hover:text-red-900 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? t('common.deleting') : t('dashboard.confirmDelete')}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  disabled={deleteMutation.isPending}
                  className="text-slate-500 hover:text-slate-700"
                >
                  {t('common.cancel')}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="text-sm text-red-600 hover:text-red-700"
              >
                {t('detail.deletePass')}
              </button>
            )}
            {deleteMutation.error ? (
              <p className="text-sm text-red-700">{describeApiError(deleteMutation.error, t)}</p>
            ) : null}
          </div>
        </div>

        {studioKind !== null ? (
          <aside className="space-y-3 lg:sticky lg:top-6 self-start">
            <div>
              <h2 className="text-sm font-medium text-slate-700">{t('detail.previewTitle')}</h2>
              <p className="text-xs text-slate-500 mt-0.5">{t('detail.previewHint')}</p>
            </div>
            {preview !== null ? (
              <PassCanvas
                design={preview}
                selected={null}
                onSelect={() => {}}
                imageUrlFor={(slot) => designImageUrl(preview, slot)}
                readOnly
              />
            ) : designQuery.isError ? (
              <p className="text-sm text-slate-500">{describeApiError(designQuery.error, t)}</p>
            ) : (
              <p className="text-sm text-slate-500">{t('common.loading')}</p>
            )}
          </aside>
        ) : null}
      </div>
    </div>
  );
}

function TravelDetailsCard({
  pass,
  lang,
  t,
}: {
  pass: DemoPass;
  lang: Lang;
  t: Translate;
}): JSX.Element {
  const visibleUntil = walletVisibleUntil(pass);
  return (
    <section className="card p-5 sm:p-6 space-y-4">
      <h2 className="font-semibold text-slate-900">{t('detail.detailsTitle')}</h2>
      <Detail label={t('detail.flight')} value={pass.flightIata ?? '—'} mono />
      <Detail
        label={t('detail.route')}
        value={
          pass.originIata && pass.destinationIata
            ? `${pass.originIata} → ${pass.destinationIata}`
            : '—'
        }
      />
      {pass.scheduledDepartureUtc && pass.originIata ? (
        <Detail
          label={t('detail.departure')}
          value={`${formatAirportLocalDateTime(pass.scheduledDepartureUtc, pass.originIata)} (${pass.originIata})`}
        />
      ) : (
        <Detail label={t('form.flightDate')} value={pass.flightDate ?? '—'} />
      )}
      {pass.scheduledArrivalUtc && pass.destinationIata ? (
        <Detail
          label={t('detail.arrival')}
          value={`${formatAirportLocalDateTime(pass.scheduledArrivalUtc, pass.destinationIata)} (${pass.destinationIata})`}
        />
      ) : null}
      {visibleUntil !== null ? (
        <Detail label={t('detail.visibleUntil')} value={formatDateTime(visibleUntil, lang)} />
      ) : null}
      <Detail label={t('detail.expires')} value={formatDateTime(pass.expiresAt, lang)} />
    </section>
  );
}

/**
 * Non-travel details — the identifying servicePayload fields (assistance:
 * name/code/access window/hotline/link; other templates: policy/plate rows)
 * instead of flight-shaped 'Járat —' placeholders. Rows render only when the
 * payload actually carries the value.
 */
function ServiceDetailsCard({
  pass,
  lang,
  t,
}: {
  pass: DemoPass;
  lang: Lang;
  t: Translate;
}): JSX.Element {
  const sp = (pass.servicePayload ?? {}) as Record<string, unknown>;
  const loungeName = payloadString(sp, 'loungeName');
  const loungeCode = payloadString(sp, 'loungeCode');
  const accessFrom = payloadString(sp, 'accessFrom');
  const accessTo = payloadString(sp, 'accessTo');
  const assistancePhone = payloadString(sp, 'assistancePhone');
  const esimLinkUrl = payloadString(sp, 'esimLinkUrl');
  const policyNumber = payloadString(sp, 'policyNumber');
  const licensePlate = payloadString(sp, 'licensePlate');
  return (
    <section className="card p-5 sm:p-6 space-y-4">
      <h2 className="font-semibold text-slate-900">{t('detail.detailsTitle')}</h2>
      {loungeName ? <Detail label={t('form.loungeName')} value={loungeName} /> : null}
      {loungeCode ? <Detail label={t('form.loungeCode')} value={loungeCode} mono /> : null}
      {policyNumber ? <Detail label={t('detail.policyNumber')} value={policyNumber} mono /> : null}
      {licensePlate ? <Detail label={t('detail.licensePlate')} value={licensePlate} mono /> : null}
      {accessFrom && accessTo ? (
        <Detail
          label={t('detail.validity')}
          value={`${formatDate(accessFrom, lang)} – ${formatDate(accessTo, lang)}`}
        />
      ) : null}
      {assistancePhone ? <Detail label={t('form.hotline')} value={assistancePhone} /> : null}
      {esimLinkUrl ? <Detail label={t('detail.relatedLink')} value={esimLinkUrl} mono /> : null}
      <Detail label={t('detail.expires')} value={formatDateTime(pass.expiresAt, lang)} />
    </section>
  );
}

function Detail({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex justify-between items-baseline gap-3">
      <span className="text-xs uppercase tracking-wider text-slate-500 font-medium shrink-0">
        {label}
      </span>
      {/* Wrap at spaces first — `break-all` split "00:30" mid-number on a
          phone; `anywhere` still breaks a long e-mail or link. */}
      <span
        className={`min-w-0 text-sm text-slate-900 font-medium text-right [overflow-wrap:anywhere] ${mono ? 'font-mono' : ''}`}
      >
        {value}
      </span>
    </div>
  );
}

function relativeTime(iso: string, t: Translate): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return t('rel.justNow');
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return t('rel.minutesAgo', { n: mins });
  const hours = Math.round(mins / 60);
  if (hours < 48) return t('rel.hoursAgo', { n: hours });
  const days = Math.round(hours / 24);
  return t('rel.daysAgo', { n: days });
}

function statePill(
  state: string,
  delayMinutes: number | null,
  t: Translate,
): { label: string; classes: string } {
  switch (state) {
    case 'on_time':
      return { label: t('status.onTime'), classes: 'bg-emerald-100 text-emerald-800' };
    case 'delayed':
      return {
        label: delayMinutes
          ? t('status.delayedMinutesLong', { n: delayMinutes })
          : t('status.delayed'),
        classes: 'bg-amber-100 text-amber-800',
      };
    case 'cancelled':
      return { label: t('status.cancelled'), classes: 'bg-red-100 text-red-800' };
    case 'boarding':
      return { label: t('status.boarding'), classes: 'bg-blue-100 text-blue-800' };
    case 'departed':
      return { label: t('status.departed'), classes: 'bg-indigo-100 text-indigo-800' };
    case 'arrived':
      return { label: t('status.arrived'), classes: 'bg-slate-100 text-slate-700' };
    case 'diverted':
      return { label: t('status.diverted'), classes: 'bg-orange-100 text-orange-800' };
    default:
      return { label: t('status.active'), classes: 'bg-slate-100 text-slate-700' };
  }
}

function LiveStatusCard({
  pass,
  lang,
  t,
}: {
  pass: DemoPass;
  lang: Lang;
  t: Translate;
}): JSX.Element {
  const live = pass.liveStatus;
  const pill = live ? statePill(live.state, live.delayMinutes, t) : null;
  // Movement wall times on each airport's own clock, as on the pass.
  const departed =
    live?.actualDeparture && pass.originIata
      ? airportLocalTime(live.actualDeparture, pass.originIata)
      : null;
  const landed =
    live?.actualArrival && pass.destinationIata
      ? airportLocalTime(live.actualArrival, pass.destinationIata)
      : null;
  return (
    <section className="card p-5 sm:p-6">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="font-semibold text-slate-900 flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          {t('detail.liveStatus')}
        </h2>
        {pill ? (
          <span
            className={`text-[10px] font-bold rounded-full px-2 py-0.5 tracking-wider ${pill.classes}`}
          >
            {pill.label}
          </span>
        ) : null}
      </div>
      {live ? (
        <>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <LiveCell
              label={t('detail.delay')}
              value={live.delayMinutes ? t('detail.delayMinutes', { n: live.delayMinutes }) : '—'}
            />
            <LiveCell label={t('detail.gate')} value={live.gate ?? '—'} />
            <LiveCell label={t('detail.terminal')} value={live.terminal ?? '—'} />
          </div>
          {departed || landed ? (
            <div className="grid grid-cols-2 gap-4 text-sm mt-3">
              {departed ? (
                <LiveCell
                  label={t('detail.actualDeparture')}
                  value={`${departed} (${pass.originIata})`}
                />
              ) : null}
              {landed ? (
                <LiveCell
                  label={t('detail.actualArrival')}
                  value={`${landed} (${pass.destinationIata})`}
                />
              ) : null}
            </div>
          ) : null}
          <div className="text-xs text-slate-400 mt-3 pt-3 border-t border-slate-100">
            {t('detail.updatedAgo', { ago: relativeTime(live.updatedAt, t) })}
            <span className="sr-only"> ({formatDateTime(live.updatedAt, lang)})</span>
          </div>
        </>
      ) : (
        <p className="text-sm text-slate-500">{t('detail.noLiveYet')}</p>
      )}
    </section>
  );
}

function LiveCell({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="text-slate-900 font-semibold mt-0.5">{value}</div>
    </div>
  );
}
